
import {Component, Prop, Vue} from "vue-property-decorator";
import {formatDate, formatDateTime} from "@/auxillary/date-format";
import {translate} from "@/auxillary/translation";
import {MdbsCore} from "@/api/tsclient";
import {DateTime} from "luxon";
import IEventReportItemDto = MdbsCore.IEventReportItemDto;
import IStudyPatientInstituteDto = MdbsCore.IStudyPatientInstituteDto;
import IStudyPatientShortDto = MdbsCore.IStudyPatientShortDto;
import DateInputMenu from "@/components/common/DateInputMenu.vue";
import SignatureDataDto = MdbsCore.SignatureDataDto;

export interface IEventReportRow {
  instituteIdentifier: string,
  instituteDisplayName: string,
  patientIdentifier: string,
  eventReportIdentifier: string,
  eventName: string,
  createdDate?: DateTime,
  sendDate?: DateTime,
  isActive: boolean,
  isDeactivated: boolean,
  deactivationUserName?: string,
  deactivationUserDisplayName?: string,
  deactivationDate?: DateTime,
  deactivationReason?: string,
  isSigned: boolean,
  signature?: SignatureDataDto,
  referenceId: string,

  instrumentId: string,
  instrumentFieldDeleted: boolean,
  patientId: string,
}

@Component({
  components: {DateInputMenu},
  filters: {
    formatDate,
    formatDateTime,
    translate
  }
})
export default class EventReportTable extends Vue {

  headers: any = [
    {text: 'Zentrum', value: 'instituteIdentifier', sortable: true, filterable: true},
    {text: 'Kennung', value: 'eventReportIdentifier', sortable: false, filterable: true, divider: false},
    {text: 'Event', value: 'eventName', width: "300px", sortable: false, filterable: false},
    {text: 'Patient', value: 'patientIdentifier', sortable: true, filterable: true, divider: true},
    {text: 'Erstellt', value: 'createdDate', sortable: true},
    {text: 'Gesendet', value: 'sendDate', sortable: true, divider: true},
    {text: 'Aktiv', value: 'isActive', sortable: false},
    {text: 'Deaktiviert', value: 'isDeactivated', sortable: false},
    {text: 'Signiert', value: 'isSigned', sortable: false},
    {text: 'Instrument', value: 'actions', sortable: false, width: "75px"},
  ];
  options: any = {
    sortBy: ['createdDate'],
    sortDesc: [true],
  };
  footerOptions: any = {
    'items-per-page-options': [15, 30]
  };

  filter: string = "";
  filterFromDate: Date | null = null;
  filterToDate: Date | null = null;

  loading: boolean = false;

  @Prop({required: false, default: () => []})
  eventReportItems!: Array<IEventReportItemDto>;

  @Prop({required: false, default: () => []})
  institutes!: Array<IStudyPatientInstituteDto>;

  @Prop({required: false, default: () => []})
  studyPatients!: Array<IStudyPatientShortDto>;

  selectedReferenceId: string = "";


  get eventReportRows(): Array<IEventReportRow> {
    const result = new Array<IEventReportRow>();

    const referenceIds = new Set<string>();
    for (let report of this.eventReportItems) {
      if (report.referenceId) {
        referenceIds.add(report.referenceId);
      }
    }

    for (let referenceId of referenceIds) {
      // show latest event report in table
      let report = this.eventReportItems
          .filter(er => er.referenceId == referenceId)
          .sort((er1, er2) => er1.reportNumber! < er2.reportNumber! ? 1 : -1)[0];
      let institute = this.institutes.find(i => i.id == report?.instituteId);
      let patient = this.studyPatients.find(p => p.id == report?.studyPatientId);

      if (!report || !institute || !patient) {
        console.error("event report data inconsistent");
        continue;
      }

      result.push({
        eventName: report.eventName ?? "",
        eventReportIdentifier: report.identifier ?? "",
        instituteIdentifier: institute.identifier ?? "",
        instituteDisplayName: institute.displayName ?? "",
        createdDate: report.creationDate ? DateTime.fromJSDate(report.creationDate) : undefined,
        sendDate: report.sendDate ? DateTime.fromJSDate(report.sendDate) : undefined,
        patientId: report.studyPatientId ?? "",
        patientIdentifier: patient.identifier ?? "",
        instrumentId: report.instrumentId ?? "",
        isActive: report.isActive ?? false,
        isDeactivated: report.isDeactivated ?? false,
        deactivationUserName: report.deactivationUserName == "Anonymous"? "System" : report.deactivationUserName ,
        deactivationUserDisplayName: report.deactivationUserDisplayName == "Anonymous User"? "System" : report.deactivationUserDisplayName,
        deactivationDate:  report.deactivationDate ? DateTime.fromJSDate( report.deactivationDate) : undefined,
        deactivationReason: report.deactivationReason,
        isSigned: report.isSigned ?? false,
        signature: report.signature,
        referenceId: report.referenceId ?? "",

        instrumentFieldDeleted: report.instrumentFieldDeleted ?? false
      })
    }

    return result;
  }

  selectReport(item: IEventReportRow) {
    if (!item) {
      return;
    }

    this.selectedReferenceId = item.referenceId;
    this.$emit("selectedRow", item.referenceId);
  }

  getRowStyle(item: IEventReportRow) {
    return item.referenceId == this.selectedReferenceId ? 'active' : ''
  }

  private resolveInstrumentRoute(item: IEventReportRow): void {
    const route: any = {
      name: 'standaloneinstrumentedit',
      params: {
        patientId: item.patientId!,
        instrumentId: item.instrumentId!
      }
    };

    this.$router.push(route);
  }

  formatDate(isoDate: string): string {
    return DateTime.fromISO(isoDate).toFormat("dd.MM.yyyy");
  }

  allowedToDates(date: string) {
    const dateObj = DateTime.fromISO(date);
    const fromDateObj = this.filterFromDate ? DateTime.fromJSDate(this.filterFromDate) : null;

    const todayOrPast = dateObj.diffNow('days').days <= 0;
    const afterFromDate = fromDateObj ? fromDateObj.diff(dateObj, 'days').days <= 0 : true;

    return todayOrPast && afterFromDate;
  }

  allowedFromDates(date: string) {
    const dateObj = DateTime.fromISO(date);
    const toDateObj = this.filterToDate ? DateTime.fromJSDate(this.filterToDate) : null;

    const todayOrPast = dateObj.diffNow('days').days <= 0;
    const beforeToDate = toDateObj ? toDateObj.diff(dateObj, 'days').days >= 0 : true;

    return todayOrPast && beforeToDate;
  }
}
